import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AlertSettings, AlertType } from 'src/app/alert/alert.service';
import { MultiSelectionIntItem } from 'src/app/models/application-models/multi-selection-int-item';
import { MultiSelectionItem } from 'src/app/models/application-models/multi-selection-item';
import { Product } from 'src/app/models/response-models/product';
import { RoutingTag } from 'src/app/models/response-models/routing-tag';
import { Vat } from 'src/app/models/response-models/vat';
import { AdjustmentService } from 'src/app/services/adjustment.service';
import { CategoryService } from 'src/app/services/category.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { GeneralFunctionsService } from 'src/app/services/general-functions.service';
import { ProductService } from 'src/app/services/product.service';

@Component({
  selector: 'app-update-selector',
  templateUrl: './update-selector.component.html',
  styleUrls: ['./update-selector.component.css']
})
export class UpdateSelectorComponent implements OnInit, OnDestroy {
  @Input() modalRef: NgbModalRef;
  @Input() products: Product[];

  @Output() loadingStatus = new EventEmitter<any>();
  @Output() transactionOk = new EventEmitter<any>();

  faExclamationTriangle = faExclamationTriangle;

  hardCodedID: string = '00000000-0000-0000-0000-000000000000';

  noneItem: MultiSelectionItem = {
    id: this.hardCodedID,
    itemName: 'None'
  }

  actionList: MultiSelectionIntItem[] = [];
  routingTagItemList: MultiSelectionItem[] = [];

  routingTagListSettings: any = {};

  action: number;

  vatList: Vat[];
  emptyVat: Vat;

  actionForm: UntypedFormGroup;
  bulkAction: UntypedFormControl = new UntypedFormControl(null, Validators.required);

  alertMain: AlertSettings;

  loadingAction: boolean;
  loadingVat: boolean;
  loadingRoutingTags: boolean;

  actionsSubscription: Subscription;
  vatSubscription: Subscription;
  routingTagsSubscription:  Subscription;

  constructor(
    private productService: ProductService,
    private errorHandlerService: ErrorHandlerService,
    private generalFunctionsService: GeneralFunctionsService,
    private adjustmentService: AdjustmentService,
    private translateService: TranslateService,
    private categoryService: CategoryService,
  ) { }

  ngOnInit(): void {
    this.loadSettings();
    this.populateActionList();
    this.loadVAT();
    this.loadRoutingTags();
  }

  loadVAT(){
    this.loadingVat = true;
    this.vatSubscription = this.adjustmentService.getVAT().subscribe({
      next: (response: Vat[])=>{
        this.vatList = response;

        this.loadingVat = false;
      },
      error: (error: any)=>{
        this.handleError(error);
        this.loadingVat = false;
      }
    });
  }

  loadRoutingTags(){
    this.loadingRoutingTags = true;
    this.routingTagsSubscription = this.categoryService.getRoutingTags().subscribe({
      next: (response: RoutingTag[])=>{
        this.routingTagItemList.push(this.noneItem);
        response.forEach((routingTag: RoutingTag)=>{
          this.routingTagItemList.push({ id: routingTag.id, itemName: routingTag.name })
        });

        this.loadingRoutingTags = false;
      },
      error: (error: any)=>{
        this.handleError(error);
        this.loadingRoutingTags = false;
      }
    });
  }

  loadSettings(){
    this.routingTagListSettings = this.createSettings('');
  }

  createSettings(placeholder: string){
    return {
      text: placeholder,
      enableSearchFilter: true,
      lazyLoading: false,
      searchAutofocus: false,
      enableCheckAll: false,
      autoPosition: true
    }
  }

  populateActionList(){
    this.actionList = this.productService.editActions;
  }

  actionChanged(){

    switch(this.bulkAction.value.id){
      case 1:
        this.actionForm = new UntypedFormGroup({
          vatRate: new UntypedFormControl(undefined , Validators.required),
        });
        break;
      case 2:
        this.actionForm = new UntypedFormGroup({
          sellOnPos: new UntypedFormControl(false),
          priceOverride: new UntypedFormControl(false),
          promptForUnit: new UntypedFormControl(false),
          promptForPrice: new UntypedFormControl(false),
          promptForAgeVerification: new UntypedFormControl(false),
          excludeFromReceipt: new UntypedFormControl(false)
        });
        break;
      case 3:
        this.actionForm = new UntypedFormGroup({
          selectedRoutingTags: new UntypedFormControl(['', Validators.required]),
        });

        this.actionForm.controls.selectedRoutingTags.setValue([this.noneItem]);
        break;
      default:
        this.actionForm = undefined;
        break;
    }
  }

  onItemSelect(item: any, UntypedFormControl: string){
    let foundItem: any = this.actionForm.controls[UntypedFormControl].value.find((formControlItem: any) => formControlItem.id === this.hardCodedID);
    if(foundItem){
      this.actionForm.controls[UntypedFormControl].setValue([item]);
    }
  }

  onDeSelectAll(items: any, UntypedFormControl: string){
    this.actionForm.controls[UntypedFormControl].setValue(items);
    this.actionForm.controls[UntypedFormControl].setValue([this.noneItem]);
  }

  runAction(){
    this.loadingAction = true;
    this.loadingStatus.emit(this.loadingAction);

    switch(this.bulkAction.value.id){
      case 1: //VAT Change
        this.products.forEach((p) => {
          p.vatRate = this.actionForm.controls.vatRate.value;
        });

        this.actionsSubscription = this.productService.bulkProductsUpdate(this.bulkAction.value.id, this.products).subscribe({
          next:() =>{
            this.loadingAction = false;
            this.generalFunctionsService.showSuccess(this.translateService.instant('shared.saveSuccess'));
            this.loadingStatus.emit(this.loadingAction);
            this.transactionOk.emit();
            this.close();
          },
          error:(error: any) =>{
            this.loadingAction = false;
            this.handleError(error);
            this.loadingStatus.emit(this.loadingAction);
          }
        });
        break;
      case 2: //Settings Change
        this.products.forEach((p) => {
          p.sellOnPOS = this.actionForm.controls.sellOnPos.value;
          p.priceOverride = this.actionForm.controls.priceOverride.value;
          p.promptForUnit = this.actionForm.controls.promptForUnit.value;
          p.promptForPrice = this.actionForm.controls.promptForPrice.value;
          p.promptForAgeVerification = this.actionForm.controls.promptForAgeVerification.value;
          p.excludeFromReceipt = this.actionForm.controls.excludeFromReceipt.value;
          p.vatRate = this.vatList[0]; // this is not saved but is required for the model to be valid
          //p.routeTags = []; // this is not saved but is required for the model to be valid
        });
        this.actionsSubscription = this.productService.bulkProductsUpdate(this.bulkAction.value.id, this.products).subscribe({
          next: () => {
            this.loadingAction = false;
            this.generalFunctionsService.showSuccess(this.translateService.instant('shared.saveSuccess'));
            this.loadingStatus.emit(this.loadingAction);
            this.transactionOk.emit();
            this.close();
          },
          error: (error: any) => {
            this.loadingAction = false;
            this.handleError(error);
            this.loadingStatus.emit(this.loadingAction);
          }
        });
          break;
        case 3: //routing tag change
          this.products.forEach((p) => {
              p.routeTags = this.actionForm.controls.selectedRoutingTags.value;
              p.vatRate = this.vatList[0]; // this is not saved but is required for the model to be valid
          });

          this.actionsSubscription = this.productService.bulkProductsUpdate(this.bulkAction.value.id, this.products).subscribe({
            next: () => {
              this.loadingAction = false;
              this.generalFunctionsService.showSuccess(this.translateService.instant('shared.saveSuccess'));
              this.loadingStatus.emit(this.loadingAction);
              this.transactionOk.emit();
              this.close();
            },
            error: (error: any) => {
              this.loadingAction = false;
              this.handleError(error);
              this.loadingStatus.emit(this.loadingAction);
            }
          });
          break;
    }

    this.loadingAction = false;
  }

  handleError(error: any){
    this.errorHandlerService.handleError(error, (message: string)=>{
      this.alertMain = {
        class: AlertType.ERROR,
        message: message
      }

      this.generalFunctionsService.showError(message);
    });
  }

  close(){
    this.modalRef.close();
  }

  ngOnDestroy(): void {
    if(this.actionsSubscription) this.actionsSubscription.unsubscribe();
    if(this.vatSubscription) this.vatSubscription.unsubscribe();
    if(this.routingTagsSubscription) this.routingTagsSubscription.unsubscribe();
  }

}
